import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { localStorageKeysToClear } from './preservedStorage';

// 캐시 관리 상수
const INACTIVITY_THRESHOLD = 60 * 60 * 1000; // 1시간 (밀리초)
const LAST_ACTIVITY_KEY = 'awana_last_activity';
const APP_VERSION_KEY = 'awana_app_version';
const RELOAD_FLAG_KEY = 'awana_reload_flag';
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

// 새로고침 플래그 확인 및 제거
const checkReloadFlag = (): boolean => {
  const reloadFlag = sessionStorage.getItem(RELOAD_FLAG_KEY);
  if (reloadFlag) {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
    return true;
  }
  return false;
};

// 마지막 활동 시간 업데이트
const updateLastActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  localStorage.setItem(APP_VERSION_KEY, APP_VERSION);
};

// 캐시 클리어 및 강제 새로고침
const clearCacheAndReload = (reason: string) => {
  console.log(`캐시 초기화 및 새로고침: ${reason}`);
  
  // 새로고침 플래그 설정 (무한 루프 방지)
  sessionStorage.setItem(RELOAD_FLAG_KEY, 'true');
  
  // Service Worker 캐시 클리어
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister();
      });
    });
  }
  
  // Cache API 클리어
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }
  
  // localStorage 및 sessionStorage 클리어 (필요한 데이터는 제외)
  // 같은 주소의 영성수련회 안내 페이지 상태와 첫 화면 팝업 "오늘 하루 보지 않기"는 남긴다(preservedStorage.ts).
  const keysToKeep = [LAST_ACTIVITY_KEY, APP_VERSION_KEY];
  localStorageKeysToClear(Object.keys(localStorage), keysToKeep).forEach((key) => {
    localStorage.removeItem(key);
  });
  
  // sessionStorage는 reload 플래그만 유지
  const reloadFlag = sessionStorage.getItem(RELOAD_FLAG_KEY);
  sessionStorage.clear();
  if (reloadFlag) {
    sessionStorage.setItem(RELOAD_FLAG_KEY, reloadFlag);
  }
  
  // 활동 시간 업데이트 (새로고침 후 다시 체크되지 않도록)
  updateLastActivity();
  
  // 약간의 지연 후 새로고침 (비동기 작업 완료 대기)
  setTimeout(() => {
    window.location.reload();
  }, 100);
};

// 앱 시작 시 캐시 체크
const checkCacheAndActivity = () => {
  // 새로고침 플래그가 있으면 체크 건너뛰기 (무한 루프 방지)
  if (checkReloadFlag()) {
    console.log('새로고침 후 재진입, 활동 시간 업데이트만 수행');
    updateLastActivity();
    return;
  }
  
  const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
  const savedVersion = localStorage.getItem(APP_VERSION_KEY);
  
  // 앱 버전이 변경된 경우 (새 배포)
  if (savedVersion && savedVersion !== APP_VERSION) {
    clearCacheAndReload('새 버전 감지');
    return;
  }
  
  // 마지막 활동 시간이 없는 경우 (첫 방문)
  if (!lastActivity) {
    updateLastActivity();
    return;
  }
  
  const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
  
  // 1시간 이상 미접속인 경우
  if (timeSinceLastActivity >= INACTIVITY_THRESHOLD) {
    clearCacheAndReload('1시간 이상 미접속');
    return;
  }
  
  // 정상 접속인 경우 마지막 활동 시간 업데이트 (너무 자주 업데이트하지 않도록)
  const lastUpdateTime = parseInt(lastActivity, 10);
  const timeSinceLastUpdate = Date.now() - lastUpdateTime;
  // 1시간 이상 지났을 때만 업데이트 (불필요한 localStorage 쓰기 방지)
  if (timeSinceLastUpdate >= INACTIVITY_THRESHOLD) {
    updateLastActivity();
  }
};

// 사용자 활동 추적
const setupActivityTracking = () => {
  const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
  
  let lastUpdateTime = 0;
  const MIN_UPDATE_INTERVAL = INACTIVITY_THRESHOLD; // 1시간마다 최대 1회 업데이트
  
  const handleActivity = () => {
    const now = Date.now();
    // 1시간 이상 지났을 때만 업데이트
    if (now - lastUpdateTime >= MIN_UPDATE_INTERVAL) {
      updateLastActivity();
      lastUpdateTime = now;
    }
  };
  
  // 이벤트 리스너 등록 (throttle 적용)
  let throttleTimer: NodeJS.Timeout | null = null;
  events.forEach((event) => {
    document.addEventListener(event, () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          handleActivity();
          throttleTimer = null;
        }, 10000); // 10초마다 최대 1회 체크
      }
    }, { passive: true });
  });
  
  // 페이지 가시성 변경 추적
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      // 페이지가 다시 보일 때 활동 시간 확인 (새로고침 플래그 확인)
      if (sessionStorage.getItem(RELOAD_FLAG_KEY)) {
        return; // 새로고침 직후면 체크 건너뛰기
      }
      
      const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
      if (lastActivity) {
        const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
        if (timeSinceLastActivity >= INACTIVITY_THRESHOLD) {
          clearCacheAndReload('페이지 재방문 시 1시간 이상 경과');
        } else {
          // 활동 시간 업데이트 (너무 자주 하지 않도록)
          const timeSinceLastUpdate = Date.now() - parseInt(lastActivity, 10);
          if (timeSinceLastUpdate >= INACTIVITY_THRESHOLD) {
            updateLastActivity();
          }
        }
      }
    }
  });
};

// 초기화
checkCacheAndActivity();
setupActivityTracking();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(); 